import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CompareGroupService } from '../comparegroupservice.service';
import { Subscription } from 'rxjs';
import { CompareGroupDTO } from '../../comparegroup/compare-group.dto';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { OrganizationDTO } from '../organization.dto';
import { ActivatedRoute, Router } from '@angular/router';
import { Logger } from '../../shared/logger/logger.component';
import { CustomOktaAuthService } from '../../auth/oktaauth.service';
import { SelfCompareGroupService } from '../selfcomparegroupservice.service';


@Component({
    selector: 'app-selfcomparegroups',
    templateUrl: './selfcomparegroups.component.html',
    styleUrls: ['./selfcomparegroups.component.scss'],
    standalone: false
})

export class SelfcomparegroupsComponent implements OnInit, OnDestroy {
  isLoading = true;
  isGroupEmpty = true;

  compareGroupsSubscription: Subscription;
  compareGroups: CompareGroupDTO[];
  orgIDsSubscription: Subscription;
  orgIDs: OrganizationDTO[];
  selectedOrg: number;
  currentOrgName: string;

  displayedColumns: string[] = ['edit', 'delete', 'compareGroupDesc', 'createdBy', 'createdOn'];
  dataSource: MatTableDataSource<CompareGroupDTO> = new MatTableDataSource([]);

  showAlert = false;
  alertIsConfirm = false;
  alertMessage = '';
  groupIDToDelete: number;

  @ViewChild(MatSort, {static: true}) sort: MatSort;

  constructor(
    private compareGroupService: CompareGroupService,
    private selfCompareGroupService: SelfCompareGroupService,
    private activatedRoute: ActivatedRoute,
    private route: Router,
    private logger: Logger,
    private oktaService: CustomOktaAuthService
  ) { }

  ngOnInit() {
    // get list of orgs when component is initialized
    this.oktaService.refreshToken('compare group init');
    this.activatedRoute.params.subscribe(
      params => {
        if (params.orgId === undefined || isNaN(params.orgId)) {
          this.selectedOrg = -1;
        } else {
          this.selectedOrg = parseInt(params.orgId, 10);
        }

        this.getOrgList();
      }
    );
  }

  getOrgList() {
    this.orgIDsSubscription = this.compareGroupService.getOrgIDs()
      .subscribe((response) => {
        response = this.sortOrgs(response);

        if(response.length == 0) {
          this.route.navigate(['/unauthorized']);
        }

        this.orgIDs = response;

        if (this.selectedOrg === -1) {
          this.selectedOrg = response[0].orgId;
        }

        this.getCompareGroups();
        this.getCurrentOrgName();
        this.isLoading = false;
      });
  }

  getCompareGroups() {
    this.compareGroupsSubscription = this.selfCompareGroupService.getSelfCompareGroups(this.selectedOrg)
      .subscribe((response) => {
        this.compareGroups = response;
        this.isGroupEmpty = this.compareGroups.length === 0;

        this.dataSource = new MatTableDataSource(response);
        this.sortDataSource();
        this.dataSource.sort = this.sort;
      });
  }

  sortDataSource() {
    this.dataSource.data.sort(
      (a, b) => {
        if ((a.compareGroupId < 0) || (b.compareGroupId < 0)) {
          if (a.compareGroupId < b.compareGroupId) {
            return -1;
          } else {
            return 1;
          }
        } else {
          if (a.compareGroupDesc < b.compareGroupDesc) {
            return -1;
          } else {
            return 1;
          }
        }
      }
    );
  }

  sortOrgs(list: OrganizationDTO[]): OrganizationDTO[] {
    // sorts organizations alphabetically
    list.sort((a, b) => {
      const nameA = a.orgName.toLowerCase();
      const nameB = b.orgName.toLowerCase();

      if (nameA < nameB) {
        return -1;
      }

      if (nameA > nameB) {
        return 1;
      }

      return 0;
    });

    return list;
  }

  applyFilter(filterValue: string) {
    // applies filter to table data
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  editCompareGroup(groupId) {
    if (groupId > 0) {
      this.route.navigate([`/selfcomparegroupdetail/${this.selectedOrg}/${groupId}`]);
    } else {
      this.logger.log('Cannot edit self compare benchmark: ' + groupId);
    }
  }

  deleteCompareGroup(groupId, groupName) {
    if (groupId > 0) {
      this.showAlert = true;
      this.alertIsConfirm = true;
      this.alertMessage = 'Are you sure you want to delete the Self Compare Benchmarks ' + groupName + '?\nThis action cannot be reversed.';
      this.groupIDToDelete = groupId;
    } else {
      this.logger.log('Cannot delete self compare benchmark: ' + groupId);
    }
  }

  createCompareGroup() {
    this.route.navigate([`/selfcomparegroupdetail/${this.selectedOrg}`]);
  }

  changeOrg(event) {
    // when org selection is changed get new benchmark groups for selected org
    this.selectedOrg = event.value;
    this.getCompareGroups();
    this.getCurrentOrgName();
  }

  getCurrentOrgName() {
    this.orgIDs.forEach(id => {
      if (id.orgId === this.selectedOrg) {
        this.currentOrgName = id.orgName;
      }
    });
  }

  closeAlert() {
    this.showAlert = false;
    this.groupIDToDelete = null;
  }

  alertConfirm(event) {
    if (event) {
      this.selfCompareGroupService.deleteCompareGroup(this.groupIDToDelete).subscribe(r => this.getCompareGroups());
    }
  }

  ngOnDestroy(): void {
    if (this.compareGroupsSubscription) {
      this.compareGroupsSubscription.unsubscribe();
    }

    this.orgIDsSubscription.unsubscribe();
  }
}
